import { omit } from 'lodash'
import { LICENSE } from '../../../util/auth.constants'

export const GWS = 'GWS'
export const OFFICE365 = 'Office365'

export const EMPLOYEES_TABLE_KEY = 'employees'

export const MODAL_NAMES = {
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  POPOUT: 'POPOUT',
  SECURE: 'SECURE',
}

export const hasDiscover = (licenses) =>
  licenses.some(
    (license) =>
      license === 'nfr-discover-essentials' || license === 'discover-essentials' || license === 'discover-premium',
  )

export const hasEngage = (licenses) =>
  licenses.some(
    (license) => license === 'nfr-engage-essentials' || license === 'engage-essentials' || license === 'engage-premium',
  )

export const hasSecureEssentials = (licenses) => licenses.some((license) => license === 'secure-essentials')

export const hasSecurePremium = (licenses) =>
  licenses.some((license) => license === 'nfr-secure-essentials' || license === 'secure-premium')

export const DISCOVER_MODULES = LICENSE.filter((license) => license.licenseType === 'discover')

export const ENGAGE_MODULES = LICENSE.filter((license) => license.licenseType === 'engage')

export const SECURE_MODULES = LICENSE.filter((license) => license.licenseType === 'secure')

const removeCountFromOptions = (options) => options.map((option) => omit(option, ['count']))

const removeCountFromFilter = (filter) => ({
  ...filter,
  options: filter.options ? removeCountFromOptions(filter.options) : undefined,
})

export const removeCountFromColumns = (columns) =>
  columns.map((column) => ({
    ...column,
    filter: column.filter ? removeCountFromFilter(column.filter) : undefined,
  }))

export const getEmployeesIdsFromIntegrationFilter = (values, integrationOptions) => {
  if (!Array.isArray(values)) {
    return []
  }

  const employeeIds =
    integrationOptions
      ?.filter((option) => values.includes(option.value))
      ?.map((option) => option.employeeIds)
      ?.flat() ?? []

  return Array.from(new Set(employeeIds))
}

// transforms redux params before sending them in the request
export const transformParams = (selectedIntegrationType) => (params) => {
  const integrationGroupsFilter = params?.filters?.['integration.groups']?.filterVal
  const integrationLicensesFilter = params?.filters?.['integration.licenseType']?.filterVal
  const integrationRolesFilter = params?.filters?.['integration.roles']?.filterVal

  const notAssignedIds = params?.filters?.notAssignedIds?.filterVal ?? null

  const filters = {
    ...params.filters,
    'integration.groups': { filterVal: null },
    'integration.licenseType': { filterVal: null },
    'integration.roles': { filterVal: null },
    notAssignedIds: { filterVal: null },
    ...(notAssignedIds && { id: { filterVal: notAssignedIds } }),
  }

  const hasIntegrationFilter =
    integrationGroupsFilter?.length || integrationRolesFilter?.length || integrationLicensesFilter?.length

  // Send integration filter values directly to backend (not employee IDs)
  // Backend will query Elasticsearch (M365) or the integration metadata (GWS) and filter accordingly
  if (integrationGroupsFilter?.length) {
    filters.groups = { filterVal: integrationGroupsFilter.join(',') }
  }
  if (integrationRolesFilter?.length) {
    filters.roles = { filterVal: integrationRolesFilter.join(',') }
  }
  if (integrationLicensesFilter?.length) {
    filters.licenseType = { filterVal: integrationLicensesFilter.join(',') }
  }
  // Tell the backend which provider (M365/GWS) these filter values belong to, so it doesn't
  // have to guess which integration to match them against.
  if (hasIntegrationFilter && selectedIntegrationType) {
    filters.integrationProviderName = { filterVal: selectedIntegrationType }
  }

  const sortField = params.sortField ?? 'name'
  const sortOrder = params.sortOrder ?? 'asc'

  return {
    ...params,
    sortField,
    sortOrder,
    filters,
  }
}

// integration
export const mergeIntegrationData = ({ data = [], selectedIntegration, selectedIntegrationType }) => {
  const groupPath = selectedIntegrationType === OFFICE365 ? 'm365Groups' : 'groups'

  return data.map((item) => ({
    ...item,
    integration: {
      groups:
        // eslint-disable-next-line no-shadow
        selectedIntegration?.[item.id]?.[groupPath]?.map((item) => item.name) ?? [],
      licenseType:
        selectedIntegration?.[item.id]?.licenseType?.map(
          // eslint-disable-next-line no-shadow
          (item) => item.displayName,
        ) ?? [],
      roles: selectedIntegration?.[item.id]?.roles ?? [],
    },
  }))
}

export const getIntegrationOptions = ({ selectedIntegration = {}, path, labelPath }) => {
  let noOptionLabel = 'None'
  if (path === 'roles') {
    noOptionLabel = 'No Roles'
  } else if (path === 'm365Groups' || path === 'groups') {
    noOptionLabel = 'No Groups'
  } else if (path === 'licenseType') {
    noOptionLabel = 'No Licenses'
  }

  const integrationOptions = {
    __noOption: {
      value: 'null',
      label: noOptionLabel,
      count: 0,
      employeeIds: [],
    },
  }

  Object.values(selectedIntegration).forEach((integration) => {
    const data = integration?.[path] ?? []

    data.forEach((item) => {
      // Handle both object arrays (groups/licenses) and string arrays (roles)
      const itemId = typeof item === 'string' ? item : item.id
      const itemLabel = typeof item === 'string' ? item : item[labelPath]

      if (integrationOptions[itemId]) {
        integrationOptions[itemId].count += 1
        integrationOptions[itemId].employeeIds.push(integration.employeeId)
      } else {
        integrationOptions[itemId] = {
          value: itemId,
          label: itemLabel,
          employeeIds: [integration.employeeId],
          count: 1,
        }
      }
    })

    if (!data?.length) {
      integrationOptions.__noOption.count += 1 // eslint-disable-line no-underscore-dangle
      integrationOptions.__noOption.employeeIds.push(integration.employeeId) // eslint-disable-line no-underscore-dangle
    }
  })

  // eslint-disable-next-line no-underscore-dangle
  if (integrationOptions.__noOption.count === 0) {
    delete integrationOptions.__noOption // eslint-disable-line no-underscore-dangle
  }

  return Object.values(integrationOptions)
}
